// V3 JOB VIEWERS *********************************************************************
// pathname: /v3/{jobSlug}/viewed-job
// ************************************************************************************************
import {
  useSwrInfiniteApi,
  ApiTalentPageV3,
  ApiTalentPageV3Ok,
} from '@hooks/useSwrApi';


// Interface
// ********************************************
interface SwrJobViewersProps {
  jobSlug: string;
  locale?: string;
  serverSideData?: ApiTalentPageV3;
  startIndex?: number;
  limit?: number;
  urlParams?: string;
}


/**
 * @description SWR: fetch the list of candidates who viewed a job, with infinite pagination.
 *
 * * **jobSlug**: string. The slug identifier for the job.
 * * **locale**: string. The locale to use for the request.
 * * **serverSideData**: ApiTalentPageV3, optional. Data from a server-side request, if available. Default undefined.
 * * **startIndex**: number, optional. The initial page number to fetch from. (ie: fetch starting with page 5). Default 1.
 * * **limit**: number, optional. The number of candidates to fetch per call. Modifies the *limit* url parameter (&limit=24). Default 24.
 * * **restUrlParams**: string. Additional URL parameters (ie: '&sort=relevant'), optional. Default empty string.
 */
export const useSwrJobViewers = (props: SwrJobViewersProps) => {
  // Props destructuring
  // ********************************************
  const {
    jobSlug,
    locale,
    serverSideData,
    startIndex = 1,
    limit = 24,
    urlParams = '',
  } = props;


  // Get data from API
  // ********************************************
  const {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    mutatePage,
    isEmpty,
    showLoadMore,
    sizeAsRef,
  } = useSwrInfiniteApi<ApiTalentPageV3Ok>({
    url: `/v3/${jobSlug}/viewed-job`,
    locale,
    urlParams,
    serverSideData,
    infiniteConfig: {
      paginationType: 'page',
      limit,
      // the key where the array of items is located in the response
      responseArrayKey: 'items',
      startIndex,
    },
    apiOptions: {
      keepPreviousData: true,
      revalidateOnFocus: false,
      dedupingInterval: 2000, // 2 seconds
      revalidateOnMount: true,
      parallel: true,
    },
    toastsOptions: {
      showErrorToast: true,
    },
  });


  // Return data
  // ********************************************
  return {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    mutatePage,
    isEmpty,
    // We need to account for the case when the data the last page perfectly fits the limit, so we use the pages property
    // if we reached the end (page === pages), we don't show the load more button (showLoadMore = false)
    showLoadMore: showLoadMore && data?.at(-1)?.page !== data?.at(-1)?.pages,
    sizeAsRef,
  };
};
